import { CopyIcon, EllipsisVerticalIcon, PencilIcon, PlayIcon, Trash2Icon } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'sonner';

import { MODAL_DELETE } from '@/components/organisms/Modals/ModalConductor';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { Simulation } from '@/modules/simulations/types';
import { popModal, pushModal } from '@/redux/modals/actions';
import { setSidebarContent, setSidebarMeta } from '@/redux/sidebar/actions';
import { selectSidebarMeta } from '@/redux/sidebar/selectors';

interface ActionsCellProps {
  simulation: Simulation;
  onRun: (id: string) => void;
  onDelete: (id: string) => void;
}

const ActionsCell = ({ simulation, onRun, onDelete }: ActionsCellProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const sidebarMeta = useSelector(selectSidebarMeta);

  const handleCopyId = async () => {
    try {
      await navigator.clipboard.writeText(simulation.simulation_id);
      toast.success(t('common.copied'));
    } catch {
      toast.error(t('common.error_copying_id'));
    }
  };

  const handleDelete = () => {
    dispatch(
      pushModal(MODAL_DELETE, {
        title: t('simulations.delete_confirm_title'),
        subtitle: (
          <Trans i18nKey="simulations.delete_confirm_description" values={[simulation.name]} />
        ),
        onDelete: () => {
          onDelete(simulation.simulation_id);
          dispatch(popModal());
          if (sidebarMeta?.simulationId === simulation.simulation_id) {
            dispatch(setSidebarContent(null));
          }
        },
      })
    );
  };

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          variant="ghost-neutral"
          size="icon-sm"
        >
          <EllipsisVerticalIcon size={16} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <DropdownMenuItem onSelect={() => onRun(simulation.simulation_id)}>
          <PlayIcon size={16} className="text-muted-foreground" />
          {t('simulations.run')}
        </DropdownMenuItem>

        <DropdownMenuItem onSelect={handleCopyId}>
          <CopyIcon size={16} className="text-muted-foreground" />
          {t('simulations.copy_id')}
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <DropdownMenuItem
          onSelect={() => {
            dispatch(setSidebarContent('edit_simulation'));
            dispatch(setSidebarMeta({ simulationId: simulation.simulation_id }));
          }}
        >
          <PencilIcon size={16} className="text-muted-foreground" />
          {`${t('common.edit')}...`}
        </DropdownMenuItem>

        <DropdownMenuItem onSelect={handleDelete} variant="destructive">
          <Trash2Icon size={16} />
          {`${t('common.delete')}...`}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ActionsCell;
